import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { normalizeJobCategory } from '@/constants/jobCategories';

// English labels exactly as seeded by backend/routes/job_status.py DEFAULT_STATUSES.
// Used to detect whether Alian has customized a default status's label (in
// which case his label always wins) or left it untouched (in which case the
// i18n bundle still gets a chance to localize it for the Spanish UI).
const DEFAULT_LABELS = {
  unscheduled: 'Unscheduled', scheduled: 'Scheduled', in_route: 'In Route',
  mast_up: 'Mast Up', mast_down: 'Mast Down', well_log: 'Well Log',
  completed: 'Completed', on_hold: 'On Hold', cancelled: 'Cancelled',
  in_dispute: 'In Dispute', in_progress: 'In Progress', draft: 'New',
  granted: 'Approved', sent_to_customer: 'Sent to Customer',
  new: 'New', in_process: 'In Process', pending: 'Pending', complete: 'Complete',
  sent: 'Sent', signed: 'Signed', submitted_for_approval: 'Submitted for Approval',
};

// Fallback statuses in case the API fails — mirrors the old hardcoded flows
// so the app never renders an empty status bar.
const FALLBACK = {
  well_construction: ['unscheduled', 'scheduled', 'in_route', 'mast_up', 'mast_down', 'well_log', 'completed', 'on_hold', 'cancelled', 'in_dispute']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'completed' || key === 'cancelled', order: i })),
  pump_installer: ['unscheduled', 'scheduled', 'in_progress', 'on_hold', 'completed', 'cancelled', 'in_dispute']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'completed' || key === 'cancelled', order: i })),
  filters_service: ['unscheduled', 'scheduled', 'in_progress', 'on_hold', 'completed', 'cancelled', 'in_dispute']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'completed' || key === 'cancelled', order: i })),
  site_inspection: ['unscheduled', 'scheduled', 'in_progress', 'on_hold', 'completed', 'cancelled', 'in_dispute']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'completed' || key === 'cancelled', order: i })),
  office: ['draft', 'in_progress', 'granted', 'sent_to_customer', 'completed', 'cancelled']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'completed' || key === 'cancelled', order: i })),
  office_workflow: ['new', 'in_process', 'pending', 'complete']
    .map((key, i) => ({ key, label: DEFAULT_LABELS[key], is_final: key === 'complete', order: i })),
  office_form: ['draft', 'sent', 'signed', 'submitted_for_approval', 'pending', 'granted']
    .map((key, i) => ({ key, label: key === 'draft' ? 'New' : (key === 'granted' ? 'Approved' : DEFAULT_LABELS[key]), is_final: key === 'granted', order: i })),
  // Fix B (2026-08-21) — Estimate workflow bar fallback, mirrors
  // backend/routes/job_status.py DEFAULT_STATUSES["estimate"].
  estimate: [
    { key: 'draft', label: 'Draft', color: 'gray' },
    { key: 'sent', label: 'Sent', color: 'blue' },
    { key: 'viewed', label: 'Viewed', color: 'sky' },
    { key: 'accepted', label: 'Accepted', color: 'green' },
    { key: 'schedule', label: 'Schedule', color: 'indigo' },
    { key: 'ready', label: 'Ready', color: 'amber' },
    { key: 'complete', label: 'Complete', color: 'green', is_final: true },
    { key: 'rejected', label: 'Rejected', color: 'red', is_final: true },
    { key: 'converted_to_invoice', label: 'Converted to Invoice', color: 'violet', is_final: true },
  ].map((s, i) => ({ ...s, is_final: !!s.is_final, order: i })),
};

let _cachedConfig = null;
let _inflight = null;

async function fetchConfig() {
  if (_cachedConfig) return _cachedConfig;
  if (_inflight) return _inflight;
  _inflight = api.get('/job-status').then(res => {
    _cachedConfig = res.data;
    _inflight = null;
    return _cachedConfig;
  }).catch(() => {
    _inflight = null;
    return FALLBACK;
  });
  return _inflight;
}

/** Hook: returns { config, loading, refresh } — config is { work_type: [statuses] } for all 4 types. */
export function useJobStatusConfig() {
  const [config, setConfig] = useState(_cachedConfig || FALLBACK);
  const [loading, setLoading] = useState(!_cachedConfig);

  useEffect(() => {
    let mounted = true;
    fetchConfig().then(cfg => { if (mounted) { setConfig(cfg); setLoading(false); } });
    return () => { mounted = false; };
  }, []);

  const refresh = async () => {
    _cachedConfig = null;
    const cfg = await fetchConfig();
    setConfig(cfg);
    return cfg;
  };

  return { config, loading, refresh };
}

/** Invalidate cache (call after editing Job Status in Settings). */
export function invalidateJobStatusCache() {
  _cachedConfig = null;
}

// Jobs of type 'permits' (Office desk work) use the 'office' status config —
// every other job category maps 1:1 to its own work_type key.
export function jobStatusWorkType(jobType) {
  const type = normalizeJobCategory(jobType);
  return type === 'permits' ? 'office' : type;
}

/** Ordered list of {key,label,is_final} for a given job, from a loaded config object. */
export function statusFlowForJob(config, job) {
  const wt = jobStatusWorkType(job?.type);
  return (config && config[wt]) || FALLBACK[wt] || [];
}

/**
 * Resolves the label to display for a status. If Alian left the default
 * seeded label untouched, we still let i18n translate it (Spanish UI keeps
 * working for the 14 pre-seeded statuses). The moment he renames it in
 * Settings > Job Status, his custom label always wins everywhere — this is
 * what makes the "administrar mis propios status" feature actually work.
 */
export function jobStatusLabel(t, s) {
  if (!s) return '';
  if (s.key === 'all') return t('jobs.statuses.all', 'All');
  const seedLabel = DEFAULT_LABELS[s.key];
  if (seedLabel && seedLabel === s.label) {
    return t(`jobs.statuses.${s.key}`, s.label);
  }
  return s.label;
}
